import React from 'react';
import { useTranslation } from 'react-i18next';
import { ListBox, Pagination, Typography } from '@heroui/react';
import { InlineSelect } from '@heroui-pro/react';
import { getNodeTestIdProp } from '@/utils';
import { useProductTableFooter } from './useProductTableFooter';
import { PRODUCT_PAGE_SIZE_OPTIONS, getPageRange } from '../productsMeta';

type ProductTableFooterProps = {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    pageWindow: (number | 'ellipsis')[];
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
};

/**
 * The products grid's footer: rows per page, which rows are showing, and the
 * page links (BOS-193).
 *
 * `ProductTable` hands it to DataGrid's `footer` slot (BOS-197), which renders
 * it as the table's own `Table.Footer`: under the rows' scroll area, at the
 * grid's full width and on its inset background, so it stays put however far
 * the rows scroll either way. The slot supplies the padding; this component
 * only lays out what goes in it.
 *
 * The page links show even when everything fits on one page, with previous and
 * next disabled: the footer keeps one shape instead of growing a control the
 * moment a filter widens the result.
 */
export const ProductTableFooter: React.FC<ProductTableFooterProps> = ({
    page,
    pageSize,
    totalCount,
    totalPages,
    pageWindow,
    onPageChange,
    onPageSizeChange,
}) => {
    const { t } = useTranslation('pages');
    const { selectedKey, onSelectionChange } = useProductTableFooter(
        pageSize,
        onPageSizeChange,
    );
    const range = getPageRange(page, pageSize, totalCount);

    const copy = React.useMemo(
        () => ({
            perPage: t('products.components.productTable.pagination.perPage'),
            previous: t('products.components.productTable.pagination.previous'),
            next: t('products.components.productTable.pagination.next'),
            summary: t('products.components.productTable.pagination.summary', {
                from: range.from,
                to: range.to,
                total: totalCount,
            }),
        }),
        [t, range.from, range.to, totalCount],
    );

    return (
        <div
            className="flex w-full flex-wrap items-center gap-x-4 gap-y-2"
            {...getNodeTestIdProp('product-table-pagination')}
        >
            <div className="flex items-center gap-2">
                <Typography type="body-xs" color="muted">
                    {copy.perPage}
                </Typography>
                <InlineSelect
                    aria-label={copy.perPage}
                    onChange={onSelectionChange}
                    value={selectedKey}
                    {...getNodeTestIdProp('product-table-per-page')}
                >
                    <InlineSelect.Trigger>
                        <InlineSelect.Value />
                        <InlineSelect.Indicator />
                    </InlineSelect.Trigger>
                    <InlineSelect.Popover className="w-[96px]">
                        <ListBox>
                            {PRODUCT_PAGE_SIZE_OPTIONS.map((size) => (
                                <ListBox.Item
                                    id={String(size)}
                                    key={size}
                                    textValue={String(size)}
                                >
                                    {size}
                                    <ListBox.ItemIndicator />
                                </ListBox.Item>
                            ))}
                        </ListBox>
                    </InlineSelect.Popover>
                </InlineSelect>
            </div>
            <Pagination className="min-w-0 flex-1" size="sm">
                <Typography
                    type="body-xs"
                    color="muted"
                    className="self-start tabular-nums sm:self-center"
                    {...getNodeTestIdProp('product-table-page-summary')}
                >
                    {copy.summary}
                </Typography>
                <Pagination.Content>
                    <Pagination.Item>
                        <Pagination.Previous
                            aria-label={copy.previous}
                            isDisabled={page <= 1}
                            onPress={() => onPageChange(page - 1)}
                            {...getNodeTestIdProp(
                                'product-table-page-previous',
                            )}
                        >
                            <Pagination.PreviousIcon />
                        </Pagination.Previous>
                    </Pagination.Item>
                    {pageWindow.map((entry, index) =>
                        entry === 'ellipsis' ? (
                            <Pagination.Item
                                key={`product-page-ellipsis-${index}`}
                            >
                                <Pagination.Ellipsis />
                            </Pagination.Item>
                        ) : (
                            <Pagination.Item key={entry}>
                                <Pagination.Link
                                    isActive={entry === page}
                                    onPress={() => onPageChange(entry)}
                                    {...getNodeTestIdProp(
                                        `product-table-page-${entry}`,
                                    )}
                                >
                                    {entry}
                                </Pagination.Link>
                            </Pagination.Item>
                        ),
                    )}
                    <Pagination.Item>
                        <Pagination.Next
                            aria-label={copy.next}
                            isDisabled={page >= totalPages}
                            onPress={() => onPageChange(page + 1)}
                            {...getNodeTestIdProp('product-table-page-next')}
                        >
                            <Pagination.NextIcon />
                        </Pagination.Next>
                    </Pagination.Item>
                </Pagination.Content>
            </Pagination>
        </div>
    );
};
